import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, MoreHorizontal, ChevronRight, ChevronDown } from 'lucide-react';
import InlineNameEditor from '../ui/InlineNameEditor';
import { SIDEBAR_TREE_ROW_ACTIVE, SIDEBAR_TREE_ROW_DROP, SIDEBAR_TREE_ROW_IDLE } from './sidebarState';
import { CollectionTreeList } from './CollectionTreeList';

// ─── CollectionBlock ──────────────────────────────────────────────────────────

export function collectionBlockPropsEqual(prev, next) {
  if (prev.collection !== next.collection) return false;
  if (prev.expanded !== next.expanded) return false;
  if (prev.onToggle !== next.onToggle) return false;
  if (prev.onCreateRequest !== next.onCreateRequest) return false;
  if (prev.onCreateFolder !== next.onCreateFolder) return false;
  if (prev.onContextMenu !== next.onContextMenu) return false;
  if (prev.disableDnd !== next.disableDnd) return false;
  if (prev.activeDragId !== next.activeDragId) return false;
  if (prev.selectedRequest !== next.selectedRequest) return false;
  if (prev.selectedCollection !== next.selectedCollection) return false;
  if (prev.selectedFolder !== next.selectedFolder) return false;
  if (prev.onSelectRequest !== next.onSelectRequest) return false;
  if (prev.onSelectCollection !== next.onSelectCollection) return false;
  if (prev.onSelectFolder !== next.onSelectFolder) return false;
  if (prev.editingItem !== next.editingItem) return false;
  if (prev.onSaveRename !== next.onSaveRename) return false;
  if (prev.onRenameDraft !== next.onRenameDraft) return false;
  if (prev.onCancelRename !== next.onCancelRename) return false;
  if (prev.isLocalWorkspace !== next.isLocalWorkspace) return false;
  if (prev.onToggleFolder !== next.onToggleFolder) return false;
  if (prev.dropIndicator !== next.dropIndicator) return false;
  if (prev.getHandleProps !== next.getHandleProps) return false;
  if (prev.fadeInIds !== next.fadeInIds) return false;
  // See folderRowPropsEqual: a boolean, never the marked Set.
  if (prev.isMarked !== next.isMarked) return false;
  if (prev.onRowClick !== next.onRowClick) return false;
  // `markedIds` IS passed down (the descendants need it) but is compared last and
  // only by identity — the Set is rebuilt on every gesture, so this line is what
  // repaints a collection's children. The rows themselves still take a boolean.
  if (prev.markedIds !== next.markedIds) return false;
  // Same reasoning as FolderRow: `expandedFolders` is one Set shared across every
  // collection, so a folder toggle anywhere else in the sidebar would otherwise
  // invalidate every other collection's memo too. Only care if one of *this*
  // collection's own folders flipped.
  if (prev.expandedFolders !== next.expandedFolders) {
    const folders = prev.collection?.settings?.folders || [];
    for (const f of folders) {
      if (prev.expandedFolders.has(f.id) !== next.expandedFolders.has(f.id)) return false;
    }
  }
  return true;
}

export const CollectionBlock = React.memo(function CollectionBlock({
  collection,
  isMarked = false,
  markedIds,
  onRowClick,
  expanded,
  onToggle,
  onCreateRequest,
  onCreateFolder,
  onContextMenu,
  onRequestDelete,
  disableDnd,
  activeDragId,
  selectedRequest,
  selectedCollection,
  selectedFolder,
  onSelectRequest,
  onSelectCollection,
  onSelectFolder,
  editingItem,
  onSaveRename,
  onRenameDraft,
  onCancelRename,
  isLocalWorkspace = false,
  expandedFolders,
  onToggleFolder,
  dropIndicator,
  getHandleProps,
  fadeInIds,
}) {
  const { t } = useTranslation();
  const canEdit    = collection.can_edit !== false;
  const dndDisabled = disableDnd || !canEdit;
  const isDragging = activeDragId === `collection-${collection.id}`;

  const ind = dropIndicator?.overId === `collection-${collection.id}` ? dropIndicator : null;
  // 'inside' = a request/folder dropped onto the collection root; before/after = collection reorder.
  const isCollectionRootDropZone = ind?.position === 'inside';
  const dropBefore = ind?.position === 'before';
  const dropAfter  = ind?.position === 'after';
  const isSelected =
    selectedCollection &&
    String(selectedCollection.id) === String(collection.id) &&
    !selectedFolder;
  const isEditing =
    editingItem?.type === 'collection' && String(editingItem.id) === String(collection.id);

  return (
    <div
      style={isDragging && !dndDisabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
      className="sidebar-row-wrap rounded-md border border-transparent"
      data-sidebar-collection={collection.id}
    >
      {dropBefore && <div className="sidebar-drop-line" aria-hidden />}
      <div
        data-drag-id={`collection-${collection.id}`}
        style={{ paddingLeft: '4px' }}
        className={`flex space-x-1 items-center px-2 py-1 rounded-md group transition-all duration-200 cursor-pointer ${
          isCollectionRootDropZone
            ? SIDEBAR_TREE_ROW_DROP
            : isSelected || isMarked
              ? SIDEBAR_TREE_ROW_ACTIVE
              : SIDEBAR_TREE_ROW_IDLE
        }`}
        role="treeitem"
        aria-selected={isMarked}
        onClick={(e) => {
          if (onRowClick?.(e, `collection-${collection.id}`)) return;
          onSelectCollection?.(collection);
        }}
        onContextMenu={(e) => onContextMenu(e, 'collection', collection)}
        /* tabIndex is what makes this row focusable AT ALL. Without it the row
           could never receive a key event, so any keyboard handler on it would
           have been dead code. */
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectCollection?.(collection); return; }
          // Delete/Backspace opens the SAME confirmation the context menu
          // uses, rather than deleting outright: these rows hold hours of
          // work, and the two most overloaded keys on the keyboard are not
          // where an irreversible action belongs. Refused while renaming, so
          // the key stays with the text field.
          if ((e.key === 'Delete' || e.key === 'Backspace') && canEdit && !isEditing) {
            e.preventDefault();
            onRequestDelete?.('collection', collection);
          }
        }}
        {...(!dndDisabled && !isEditing ? getHandleProps(`collection-${collection.id}`) : {})}
      >
        <button
          type="button"
          className="sidebar-collection-chevron-btn mr-1 shrink-0 flex items-center p-0.5 rounded"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onToggle(collection.id); }}
          aria-label={expanded ? t('sidebar.collapseCollection') : t('sidebar.expandCollection')}
        >
          {expanded ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
        </button>
        {isEditing ? (
          <InlineNameEditor
            value={collection.name}
            onSave={(name) => onSaveRename?.('collection', collection.id, name)}
            onDraftChange={(draft) => onRenameDraft?.('collection', collection.id, draft)}
            onCancel={() => onCancelRename?.()}
          />
        ) : (
          <span className="flex-1 min-w-0 text-xs font-medium text-gray-800 dark:text-zinc-200 truncate">
            {collection.name}
          </span>
        )}
        <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onCreateRequest(collection.id); }}
            className="p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
            title={t('sidebar.addRequest')}
          >
            <Plus size={12} className="text-gray-600 dark:text-zinc-400" />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onContextMenu(e, 'collection', collection); }}
            className="p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer"
          >
            <MoreHorizontal size={12} className="text-gray-600 dark:text-zinc-400" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="sidebar-folder-scope animate-slideIn">
          <CollectionTreeList
            collection={collection}
            markedIds={markedIds}
            onRowClick={onRowClick}
            parentId={null}
            depth={0}
            expandedFolders={expandedFolders}
            onToggleFolder={onToggleFolder}
            onCreateRequest={onCreateRequest}
            onContextMenu={onContextMenu}
            disableDnd={disableDnd}
            activeDragId={activeDragId}
            selectedRequest={selectedRequest}
            selectedFolder={selectedFolder}
            onSelectRequest={onSelectRequest}
            onSelectFolder={onSelectFolder}
            editingItem={editingItem}
            onSaveRename={onSaveRename}
            onRenameDraft={onRenameDraft}
            onCancelRename={onCancelRename}
            dropIndicator={dropIndicator}
            getHandleProps={getHandleProps}
            fadeInIds={fadeInIds}
          />
        </div>
      )}
      {dropAfter && <div className="sidebar-drop-line" aria-hidden />}
    </div>
  );
}, collectionBlockPropsEqual);
